/**
 * Writing and speaking: the text box, the on-screen keyboard, the microphone switch and the stop
 * button (cut the reply being spoken). The microphone exists only where there is voice: the kiosk
 * role, and a mode that allows it.
 *
 * Two looks, same behaviour:
 *  - standard  the old kiosk's box: a card with the keyboard button and a filled send button;
 *  - pill      (Essenziale) an outlined pill like the main bar's, only the text and a send icon.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../../react/app';
import { useAppearance, useMode, useSession, useTranslate } from '../../react/hooks';
import { useVoice } from '../../voice/useVoice';
import { Icon } from '../../ui-kit/Icon';
import { Keyboard } from '../../ui-kit/Keyboard';
import { useOverlayRoot } from '../../shell/overlay-root';
import css from './Composer.module.css';

type Look = 'standard' | 'pill';

export function ComposerModule() {
  return <Composer look="standard" />;
}

export function PillComposer() {
  return <Composer look="pill" />;
}

function Composer({ look }: { look: Look }) {
  const { kiosk, voice } = useApp();
  const t = useTranslate();
  const mode = useMode();
  const appearance = useAppearance();
  const keyboardButton = look === 'standard' && appearance.keyboardButton;
  const [text, setText] = useState('');
  const [keyboard, setKeyboard] = useState(false);
  // a new screen (the data filled in, the summary next) closes the keyboard: adjusted while rendering
  const screen = useSession((s) => s?.state ?? null);
  const [keyboardScreen, setKeyboardScreen] = useState(screen);
  if (screen !== keyboardScreen) {
    setKeyboardScreen(screen);
    setKeyboard(false);
  }
  const readOnly = kiosk.role === 'replay';
  const overlayRoot = useOverlayRoot();
  // the large keyboard on the canvas's bottom edge (07), the small one above the box; no layer yet: the small one
  const atBottom = appearance.keyboardPlace === 'bottom' && overlayRoot !== null;
  const box = useRef<HTMLDivElement>(null);
  const keys = useRef<HTMLDivElement>(null);
  // a tap anywhere else closes it, as a phone's does
  useEffect(() => {
    if (!keyboard) return;
    const away = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!box.current?.contains(target) && !keys.current?.contains(target)) setKeyboard(false);
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [keyboard]);

  const send = () => {
    const words = text.trim();
    if (!words) return;
    voice?.unlockAudio();
    void kiosk.say(words);
    setText('');
    setKeyboard(false); // said: the keyboard goes, however it was sent (its Invia, the box's ➤, a real Enter)
  };

  const keyboardView = (
    <div className={atBottom ? css.keyboardBottom : css.keyboardDock} ref={keys} data-keyboard-place={atBottom ? 'bottom' : 'box'}>
      <Keyboard layout="message" value={text} onChange={setText} onEnter={send}
        spaceLabel={t('keyboard.space')} doneLabel={t('chat.send')} />
    </div>
  );

  return (
    <div className={`${css.composer} ${look === 'pill' ? css.pill : ''}`} ref={box}>
      {keyboard && (atBottom ? createPortal(keyboardView, overlayRoot) : keyboardView)}
      <form className={css.row} onSubmit={(e) => { e.preventDefault(); send(); }}>
        {keyboardButton && (
          <button type="button" className={`${css.round} ${keyboard ? css.on : ''}`} onClick={() => setKeyboard((k) => !k)}
            aria-label="keyboard" disabled={readOnly}>
            <Icon name="keyboard" size={26} />
          </button>
        )}
        <input
          className={css.input}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => voice?.unlockAudio()}
          // with the kiosk's keyboard the system one never opens (a phone): tapping the box opens ours (07 D-08)
          inputMode={keyboardButton ? 'none' : undefined}
          onPointerUp={keyboardButton && !readOnly ? () => setKeyboard(true) : undefined}
          placeholder={t('chat.placeholder')}
          disabled={readOnly}
          enterKeyHint="send"
        />
        <button type="submit" className={`${css.round} ${css.send}`} disabled={readOnly || !text.trim()} aria-label={t('chat.send')}>
          <Icon name="send" size={26} />
        </button>
        {voice && <VoiceButtons microphoneAllowed={mode.behaviour.microphone === 'allowed'} />}
      </form>
    </div>
  );
}

function VoiceButtons({ microphoneAllowed }: { microphoneAllowed: boolean }) {
  const { voice } = useApp();
  const t = useTranslate();
  const state = useVoice((v) => v);
  // A mode without voice (hearing: the backend turns it off too) switches a live microphone off.
  useEffect(() => {
    if (!microphoneAllowed && state.microphone) void voice?.setMicrophone(false);
  }, [microphoneAllowed, state.microphone, voice]);
  if (!voice) return null;
  const speaking = state.replying;
  const label = !microphoneAllowed ? t('voice.disabled_by_mode') : state.error ? t('voice.error') : state.microphone ? t('voice.mic_on') : t('voice.mic_off');
  return (
    <>
      <button
        type="button"
        className={`${css.round} ${css.mic} ${state.microphone ? css.live : ''} ${state.activity === 'listening' ? css.listening : ''}`}
        onClick={() => { voice.unlockAudio(); void voice.setMicrophone(!state.microphone); }}
        disabled={!microphoneAllowed}
        aria-pressed={state.microphone}
        aria-label={label}
        title={label}
      >
        <Icon name={state.microphone ? 'mic' : 'mic-off'} size={26} />
      </button>
      {/* only while he speaks, right of the microphone: cut the reply */}
      {speaking && (
        <button type="button" className={`${css.round} ${css.stop}`} onClick={() => voice.stopSpeaking()} aria-label={t('voice.stop')}>
          <Icon name="stop" size={26} />
        </button>
      )}
    </>
  );
}
